import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnalyticsActionsResponse,
  AnalyticsBranchesResponse,
  AnalyticsSummaryResponse,
  AnalyticsTimeSeriesResponse,
  AnalyticsTranslationHealthResponse,
  AnalyticsWorkspacesResponse,
} from "../../../api/v2/types";
import { localizationClient } from "../../../api/v2/localizationClient";
import { parseApiError } from "../../../api/http/errors";
import { AnalyticsFiltersState } from "../types";

const nowIsoDate = () => new Date().toISOString().slice(0, 10);
const defaultFromIsoDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return date.toISOString().slice(0, 10);
};

const initialFilters: AnalyticsFiltersState = {
  workspaceScope: "current",
  from: defaultFromIsoDate(),
  to: nowIsoDate(),
  interval: "day",
  metric: "events",
};

interface UseRepositoryAnalyticsArgs {
  owner: string;
  repo: string;
  workspaceKey: string;
}

export const useRepositoryAnalytics = ({ owner, repo, workspaceKey }: UseRepositoryAnalyticsArgs) => {
  const [filters, setFilters] = useState<AnalyticsFiltersState>(initialFilters);
  const [summary, setSummary] = useState<AnalyticsSummaryResponse | null>(null);
  const [timeseries, setTimeseries] = useState<AnalyticsTimeSeriesResponse | null>(null);
  const [actions, setActions] = useState<AnalyticsActionsResponse | null>(null);
  const [workspaces, setWorkspaces] = useState<AnalyticsWorkspacesResponse | null>(null);
  const [translationHealth, setTranslationHealth] = useState<AnalyticsTranslationHealthResponse | null>(null);
  const [branches, setBranches] = useState<AnalyticsBranchesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const refreshTimerRef = useRef<number | null>(null);

  const baseParams = useMemo(() => {
    const useWorkspace = filters.workspaceScope === "current";
    return {
      workspaceKey: useWorkspace ? workspaceKey : undefined,
      from: filters.from ? `${filters.from}T00:00:00.000Z` : undefined,
      to: filters.to ? `${filters.to}T23:59:59.999Z` : undefined,
    };
  }, [filters.workspaceScope, filters.from, filters.to, workspaceKey]);

  const loadAnalytics = useCallback(async () => {
    if (!owner || !repo) return;
    setIsLoading(true);
    setErrorText("");
    try {
      const summaryData = await localizationClient.getAnalyticsSummary(owner, repo, baseParams);
      setSummary(summaryData);

      const timeseriesData = await localizationClient.getAnalyticsTimeSeries(owner, repo, {
        ...baseParams,
        interval: filters.interval,
        metric: filters.metric,
      });
      setTimeseries(timeseriesData);

      const [actionsData, workspacesData] = await Promise.all([
        localizationClient.getAnalyticsActions(owner, repo, baseParams),
        localizationClient.getAnalyticsWorkspaces(owner, repo, baseParams),
      ]);
      setActions(actionsData);
      setWorkspaces(workspacesData);

      const [translationHealthData, branchesData] = await Promise.all([
        localizationClient.getAnalyticsTranslationHealth(owner, repo, {
          ...baseParams,
          workspaceKey,
        }),
        localizationClient.getAnalyticsBranches(owner, repo, baseParams),
      ]);
      setTranslationHealth(translationHealthData);
      setBranches(branchesData);
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
    } finally {
      setIsLoading(false);
    }
  }, [owner, repo, workspaceKey, baseParams, filters.interval, filters.metric]);

  useEffect(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      loadAnalytics();
    }, 250);
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, [loadAnalytics]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadAnalytics();
    }, 45000);
    return () => window.clearInterval(interval);
  }, [loadAnalytics]);

  return {
    filters,
    setFilters,
    summary,
    timeseries,
    actions,
    workspaces,
    translationHealth,
    branches,
    isLoading,
    errorText,
    refresh: loadAnalytics,
  };
};
